import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Divider,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  IconButton,
  Tooltip,
} from "@mui/material";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import moment from "moment";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { AxiosError } from "axios";
import { VisibilityOutlined } from "@mui/icons-material";
import MUIDataTable from "mui-datatables";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface Asset {
  last_visited: string | number | Date;
  asset_service_id: string;
  asset_type_name: string;
  asset_name: string;
  state_name: string;
  city_name: string;
  region_name: string;
  address_type: string;
  address: string;
  customer_name: string;
  customer_phone: string;
  type: string;
  subscription_creation_date: string | Date;
  subscription_type: string;
  subscription_start_date: string | Date;
  subscription_end_date: string | Date;
  asset_image: string;
  address_id: string;
  customer_id: string;
  asset_purchased_date: string | number | Date;
  asset_type_id: string;
  asset_id: string;
  image_type: string;
  division_name: string;
  asset_sku: string;
}

interface Subscription {
  subscription_id?: number; // Added for unique key
  subscription_type_name: string;
  created_at: string | Date;
  end_date: string | Date;
  is_active: boolean;
}

interface Ticket {
  ticket_id: number;
  title: string;
  ticket_service_id: string;
  customer_name: string;
  asset_name: string;
  customer_address: string;
  assignee: string;
  status_name: string;
  created_at: string | Date;
}

// Extracted component for address tooltip
interface AddressTooltipCellProps {
  value: string;
}

const AddressTooltipCell: React.FC<AddressTooltipCellProps> = ({ value }) => {
  return (
    <Tooltip title={value}>
      <span>{value.length > 20 ? `${value.substring(0, 20)}...` : value}</span>
    </Tooltip>
  );
};

// Extracted component for action buttons
interface ActionButtonCellProps {
  ticketId: number;
  onNavigate: (ticketId: number) => void;
}

const ActionButtonCell: React.FC<ActionButtonCellProps> = ({
  ticketId,
  onNavigate,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Tooltip title="View Ticket">
        <IconButton aria-label="view" onClick={() => onNavigate(ticketId)}>
          <VisibilityOutlined />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const CustomerViewAsset: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { fetchError } = useSnackbar();
  const navigate = useNavigate();
  const [assetData, setAssetData] = useState<Asset | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);

  const handleMediaOpen = (fileName: string) => {
    const url = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${fileName}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setLoadingTickets(true);
      try {
        const [assetResponse, subscriptionResponse, ticketsResponse] =
          await Promise.all([
            axios.get(`/assets/${id}`),
            axios.get(`/subscription/assets/${id}`),
            axios.get(`/tickets/asset/${id}`),
          ]);

        setAssetData(assetResponse?.data);
        setSubscriptions(subscriptionResponse?.data?.list || []);
        setTickets(ticketsResponse?.data?.list || []);
      } catch (error) {
        navigate("/customer/assets-list");
        if (error instanceof AxiosError && error.response) {
          fetchError(error.response?.data?.error || error.message);
        }
      } finally {
        setLoading(false);
        setLoadingTickets(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, fetchError, navigate]);

  // Fixed: Render functions for custom cells
  const renderAddressCell = (value: string) => {
    return <AddressTooltipCell value={value} />;
  };

  const renderActionCell = (ticketId: number) => {
    return (
      <ActionButtonCell
        ticketId={ticketId}
        onNavigate={(id) => navigate(`/admin/ticket-view/${id}`)}
      />
    );
  };

  // Fixed: Render tickets section with proper conditional logic
  const renderTicketsSection = () => {
    if (loadingTickets) {
      return (
        <>
          <Divider sx={{ height: 2, backgroundColor: "blue", mt: 3 }} />
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <Typography variant="body1">Loading Tickets...</Typography>
          </Box>
        </>
      );
    }

    if (tickets.length === 0) {
      return null;
    }

    return (
      <>
        <Divider sx={{ height: 2, backgroundColor: "blue", mt: 3 }} />
        <ThemeProvider theme={getMuiTheme}>
          <MUIDataTable
            title={"Tickets"}
            data={tickets.map((ticket) => ({
              ...ticket,
              title: ticket.title || "-",
              customer_name: ticket.customer_name || "-",
              asset_name: ticket.asset_name || "-",
              address: `${ticket.customer_address || ""}`,
              assignee: ticket.assignee || "Unassigned",
              created_at: moment(ticket.created_at).format("MMM D, YYYY"),
            }))}
            columns={[
              { name: "title", label: "Title" },
              { name: "ticket_service_id", label: "Service Id" },
              { name: "asset_name", label: "Asset Type" },
              {
                name: "address",
                label: "Address",
                options: {
                  customBodyRender: renderAddressCell,
                },
              },
              { name: "status_name", label: "Status" },
              { name: "assignee", label: "Assignee" },
              { name: "created_at", label: "Created At" },
              {
                name: "ticket_id",
                label: "Actions",
                options: {
                  filter: false,
                  sort: false,
                  customBodyRender: renderActionCell,
                },
              },
            ]}
            options={{
              selectableRows: "none",
              elevation: 0,
              download: true,
              print: true,
              filter: true,
              search: true,
              responsive: "standard",
            }}
          />
        </ThemeProvider>
      </>
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        className="view-ticket"
        style={{
          padding: "24px",
          width: "100%",
          borderRadius: "8px",
        }}
      >
        <Grid
          item
          xs={12}
          display={"flex"}
          justifyContent={"center"}
          className="view-ticket-title"
        >
          <IconButton className="back-btn">
            <ArrowBackIcon onClick={() => navigate("/customer/assets-list")} />
          </IconButton>
          <Typography variant="h5" gutterBottom>
            Asset Details
          </Typography>
        </Grid>
        <Grid container className="view-ticket-details">
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Asset Id</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.asset_service_id}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Brand Name</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.asset_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.customer_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer Phone</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.customer_phone}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Address</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {`${assetData?.address}, ${assetData?.city_name}, ${assetData?.state_name}, ${assetData?.address_type}`}
                </span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Subscription Type</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.subscription_type}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Subscription Start Date</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {assetData?.subscription_start_date
                    ? moment(assetData?.subscription_start_date).format(
                        "MMM D, YYYY"
                      )
                    : ""}
                </span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Subscription End Date</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {assetData?.subscription_end_date
                    ? moment(assetData?.subscription_end_date).format(
                        "MMM D, YYYY"
                      )
                    : ""}
                </span>
              </Box>
            </Typography>
          </Grid>
          {assetData?.type === "company" && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Division</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {assetData?.division_name}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>SKU</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.asset_sku}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Last Visited On</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :
                  {assetData?.last_visited
                    ? moment(assetData?.last_visited).format("MMM D, YYYY")
                    : "No Tickets"}
                </span>
              </Box>
            </Typography>
          </Grid>
        </Grid>

        {/* Subscription History Section */}
        {subscriptions && subscriptions.length > 0 && (
          <>
            <Divider sx={{ height: 2, backgroundColor: "blue", my: 2 }} />
            <Typography variant="h5" gutterBottom>
              Subscription History
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ display: "table-row-group" }}>
                  <TableRow>
                    <TableCell>Subscription Type</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Fixed: Use subscription_id or created_at for unique keys */}
                  {subscriptions.map((data, index) => (
                    <TableRow
                      key={
                        data.subscription_id || `${data.created_at}-${index}`
                      }
                    >
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                          <Box>
                            <span>{String(data?.subscription_type_name)}</span>
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                          <Box>
                            <span>
                              {moment(data?.created_at).format("MMM D, YYYY")}
                            </span>
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                          <Box>
                            <span>
                              {moment(data?.end_date).format("MMM D, YYYY")}
                            </span>
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                          <Box>
                            <span>
                              {data?.is_active ? "Active" : "In-Active"}
                            </span>
                          </Box>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Fixed: Extracted nested ternary into function */}
        {renderTicketsSection()}

        {/* Asset Media Section */}
        <Divider sx={{ height: 2, backgroundColor: "blue", my: 2 }} />
        <Typography variant="h5" gutterBottom>
          Asset Media
        </Typography>
        <div
          style={{ justifyContent: "center", display: "flex" }}
          className="ticket-media"
        >
          {assetData?.asset_image ? (
            <div className="media-container view-media">
              {/* Fixed: Wrapped image in button for accessibility */}
              <button
                onClick={() => handleMediaOpen(assetData?.asset_image)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  display: "inline-block",
                }}
                aria-label="View full size asset image"
              >
                <img
                  className="upload-img"
                  src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${assetData?.asset_image}`}
                  alt="lift-media"
                  style={{ display: "block" }}
                />
              </button>
            </div>
          ) : (
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              No media available
            </Typography>
          )}
        </div>
      </Card>
    </DashboardLayout>
  );
};

export default CustomerViewAsset;
